'use client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import { saveSearchQuery } from '@util/firestore/search';
import { searchSellers } from '@util/firestore/users/user.service';
import useDebounce from '@util/hooks/useDebounce';
import useSearchHistory from '@util/hooks/useSearchHistory';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import { useToastContext } from 'context/ToastContext';
import { useQuerySuggestions } from 'features/search';
import { newSearchHref } from 'features/search/service/searchHref';
import { deleteSuggestion } from 'features/search/service/suggestions.server';
import type { BaseHit } from 'instantsearch.js';
import { Hit as SearchHit } from 'instantsearch.js';
import type { SendEventForHits } from 'instantsearch.js/es/lib/utils';
import { MotionDiv } from 'motion';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { HitsProps, useHits } from 'react-instantsearch';
import { SearchResponseHit } from 'typesense/lib/Typesense/Documents';

// The vehicle a shopper is filtering by has to survive jumping to another
// keyword, otherwise the fitment card keeps showing the bike while the results
// stop being narrowed by it. The last query's own filters do not survive, which
// is what `newSearchHref` is for — it is the same URL typing the query builds.
const useKeywordHref = () => {
  const searchParams = useSearchParams();

  return useCallback(
    (keyword: string) =>
      newSearchHref(
        keyword,
        new URLSearchParams(searchParams?.toString() ?? '')
      ),
    [searchParams]
  );
};

// HACK: some very odd wizardry to be able to create a CustomHits component with non-optional hitComponent
//       perhaps refactor this somewhere else along w/ its imports.
type CustomHitsProps<THit extends BaseHit> = HitsProps<THit> &
  Omit<HitsProps<THit>, 'hitComponent'> & {
    hitComponent: React.JSXElementConstructor<{
      hit: THit;
      sendEvent: SendEventForHits;
    }>;
  };

function LastSearchSuggestions({
  searchHistory,
  setShowSearchPreviewAction,
}: {
  searchHistory: string[];
  setShowSearchPreviewAction: (val: boolean) => void;
}) {
  const { userDoc } = useAuth();
  const getKeywordHref = useKeywordHref();
  return (
    <div className="min-w-[38rem] max-w-[48rem] pr-[3rem] sm:min-w-full sm:max-w-full sm:pr-0">
      <div className="mt-[1rem] flex flex-col">
        <div className="flex flex-col gap-y-2">
          {searchHistory.length > 0 ? (
            searchHistory.slice(0, 6).map((search, index) => (
              <Link
                href={getKeywordHref(search)}
                onClick={() => {
                  setShowSearchPreviewAction(false);
                }}
                className="flex w-full cursor-pointer flex-row items-center justify-between truncate whitespace-nowrap rounded-[1rem] px-5 py-4 scrollbar-none hover:bg-brand-lightest-gray sm:gap-x-4 lg:whitespace-normal lg:pl-[3rem]"
                key={`${search}-${index}`}
              >
                <span className="mr-[1rem] flex grow">{`${search}`}</span>
              </Link>
            ))
          ) : !!userDoc ? (
            <p>No search history yet!</p>
          ) : (
            <div className="mt-4">
              <Button
                href="/signup"
                height="small"
                width="small"
                type="secondary"
                text="Sign up to see search history"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CustomHits = (props: CustomHitsProps<any>) => {
  const { hits, sendEvent } = useHits(props);

  return (
    <>
      {hits.length !== 0 ? (
        <div className="flex flex-col gap-y-[.3rem]">
          {hits.map((hit, index) => (
            <props.hitComponent sendEvent={sendEvent} key={index} hit={hit} />
          ))}
        </div>
      ) : (
        <div className="m-[1rem] flex justify-center rounded-2xl bg-brand-lightest-gray p-[2rem]">
          <span>
            No matches found for your search query. Please try another keyword.
          </span>
        </div>
      )}
    </>
  );
};

interface CustomHitProps<THit extends BaseHit> {
  hit: THit;
  sendEvent: (type: string, hit: SearchHit<THit>, eventName: string) => void;
}

function SellerHit({ hit, sendEvent }: CustomHitProps<any>) {
  return (
    <>
      <Link
        key={hit.document.uid}
        href={'/profile/' + hit.document.username_slug}
        className="relative flex w-full cursor-pointer select-none items-center rounded-2xl py-3 pl-2 pr-4 hover:bg-brand-lightest-gray sm:pl-0 lg:pl-[2rem]"
        onClick={() => {
          sendEvent('click', hit, 'seller hit clicked');
        }}
      >
        <div className="flex items-center gap-[1rem]">
          <Avatar user={hit.document} size="extraSmall" />

          <div className="flex flex-col">
            <span className="text-[1.7rem] font-medium text-zinc-600">
              {hit.document.username}
            </span>
          </div>
        </div>
      </Link>
    </>
  );
}

const Suggestions = ({
  setShowSearchPreviewAction,
}: {
  setShowSearchPreviewAction: (val: boolean) => void;
}) => {
  const { searchTerm, setSearchTerm } = useSearch();
  const { showErrorToast } = useToastContext();
  const queryClient = useQueryClient();
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const { suggestions } = useQuerySuggestions(debouncedSearchTerm ?? '');
  const { userDoc } = useAuth();
  const getKeywordHref = useKeywordHref();
  return (
    <div className="mt-4 flex min-w-full items-center scrollbar-none lg:px-4">
      <div className="flex min-w-full flex-col">
        {suggestions.map((search) => {
          return (
            <div
              key={search.document.id}
              className="flex min-w-full cursor-pointer flex-row items-center justify-between"
            >
              <Link
                href={getKeywordHref(search.document.q)}
                className="w-full truncate whitespace-nowrap rounded-[1rem] px-5 py-4 scrollbar-none hover:cursor-pointer hover:bg-brand-lightest-gray sm:gap-x-4 lg:whitespace-normal"
                onClick={() => {
                  if (userDoc) {
                    saveSearchQuery({
                      uid: userDoc.uid,
                      query: search.document.q,
                    });
                    window.localStorage.setItem(
                      'from_suggestion',
                      search.document.q
                    );
                  }
                  setSearchTerm(search.document.q);
                  setShowSearchPreviewAction(false);
                }}
              >
                <div className="hover w-full">
                  {search.highlight.q?.snippet ? (
                    <div className="flex w-full flex-nowrap items-center truncate text-ellipsis whitespace-nowrap">
                      <span
                        className="highlighted-search"
                        dangerouslySetInnerHTML={{
                          __html: search.highlight.q.snippet,
                        }}
                      ></span>
                    </div>
                  ) : (
                    <div className="flex w-full flex-nowrap items-center gap-x-2 truncate whitespace-nowrap">
                      {search.document.q}
                    </div>
                  )}
                </div>
              </Link>
              {!!userDoc?.roles?.admin && !!debouncedSearchTerm && (
                <Button
                  onClick={() => {
                    const confirmed = confirm(
                      `Are you sure you want to delete "${search.document.q}"?`
                    );
                    if (!confirmed) return;
                    const deletedId = search.document.id;
                    deleteSuggestion(deletedId)
                      .then(() => {
                        queryClient.setQueryData<
                          SearchResponseHit<{ id: string }>[]
                        >(['suggestions', debouncedSearchTerm], (oldData) => {
                          if (!oldData) return [];
                          return oldData.filter(
                            (i) => i.document.id !== deletedId
                          );
                        });
                      })
                      .catch((error) => {
                        console.error(error);
                        showErrorToast('Failed to delete suggestion');
                      });
                  }}
                  text="Delete Suggestion"
                  type="decline"
                  height="small"
                  width="small"
                  className="rounded-full border-none hover:bg-brand-red hover:text-brand-white"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SearchResultsPreview = ({
  setShowSearchPreviewAction,
}: {
  setShowSearchPreviewAction: (val: boolean) => void;
}) => {
  const { debouncedSearchTerm } = useSearch();
  const { user } = useAuth();
  const previewRef = useRef<HTMLDivElement>(null);

  const { searches: searchHistory } = useSearchHistory({ uid: user?.uid });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile()) return;
      if (
        previewRef.current &&
        !previewRef.current.contains(event.target as Node)
      ) {
        const searchInput = document.getElementById('search-input');
        if (searchInput && !searchInput.contains(event.target as Node)) {
          setShowSearchPreviewAction(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowSearchPreviewAction]);

  return (
    <>
      {debouncedSearchTerm ? (
        <MotionDiv
          ref={previewRef}
          initial={{ opacity: 0, y: -10, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed left-1/2 top-0 z-[50] flex h-screen w-full flex-col overflow-x-hidden overflow-y-hidden rounded-2xl bg-white p-4 pt-[8rem] shadow-xl scrollbar-none sm:absolute sm:top-[1rem] sm:overflow-y-scroll sm:pt-0 lg:h-fit lg:w-[91.3rem]"
        >
          <div className="mt-4 sm:mt-[5rem]">
            <Suggestions
              setShowSearchPreviewAction={setShowSearchPreviewAction}
            />
          </div>

          <div className="py-[1rem] sm:p-[1rem]">
            <SectionTitle title="Sellers" />
          </div>

          <SearchSellers />
        </MotionDiv>
      ) : (
        <MotionDiv
          ref={previewRef}
          initial={{ opacity: 0, y: -10, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed left-1/2 top-0 z-[50] flex h-screen w-full flex-col overflow-x-hidden overflow-y-hidden rounded-2xl bg-white p-4 pt-[4rem] shadow-xl scrollbar-none sm:absolute sm:top-[1rem] sm:overflow-y-scroll sm:pt-0 lg:h-fit lg:w-[91.3rem]"
        >
          {searchHistory.length ? (
            <div className="mt-[7rem] flex flex-col gap-y-2">
              <SectionTitle title="Recent Searches" />

              <LastSearchSuggestions
                searchHistory={searchHistory}
                setShowSearchPreviewAction={setShowSearchPreviewAction}
              />
            </div>
          ) : (
            <div className="flex h-[20rem] w-full items-center justify-center">
              <h3 className="text-brand text-[2rem] font-medium text-zinc-500">
                Search for anything
              </h3>
            </div>
          )}
        </MotionDiv>
      )}
    </>
  );
};

const SectionTitle = ({
  title,
  helperElement,
  hideHelperElement = false,
}: {
  title: string;
  helperElement?: React.ReactNode;
  hideHelperElement?: boolean;
}) => {
  if (helperElement && !hideHelperElement) {
    return (
      <div className="flex w-full flex-row items-center justify-between px-[1rem]">
        <p className="text-brand mb-1 block text-[1.8rem] font-semibold text-zinc-600">
          {title}
        </p>

        {helperElement}
      </div>
    );
  }

  return (
    <div className="w-full px-[1rem]">
      <p className="text-[1.3rem] font-bold uppercase text-zinc-500">{title}</p>
    </div>
  );
};

const SearchSellers = () => {
  const { debouncedSearchTerm } = useSearch();
  const { data: sellers } = useQuery({
    queryKey: ['sellers', debouncedSearchTerm],
    queryFn: () => searchSellers(debouncedSearchTerm),
  });
  return sellers?.map((seller) => (
    <SellerHit key={seller.document.id} hit={seller} sendEvent={() => {}} />
  ));
};

export default SearchResultsPreview;
